import {
  LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX,
  LOCAL_PRODUCT_IMAGES_STORE,
  openLocalProductsDb,
  requestToPromise,
  txDone,
  type IdbFactory,
} from '../productRepository/localProductsDb';
import type {
  LocalProductImageRecord,
  ProductImageBatch,
  ProductImageStore,
} from './types';

/**
 * IndexedDB-backed package-photo store in `fbc-local-products` / `productImages`.
 */
export function createIdbProductImageStore(options?: {
  indexedDB?: IdbFactory;
}): ProductImageStore {
  const factory = options?.indexedDB;

  return {
    async listByProductId(productId: string) {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_PRODUCT_IMAGES_STORE, 'readonly');
        const store = tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE);
        const index = store.index(LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX);
        const rows = await requestToPromise(index.getAll(productId));
        await txDone(tx);
        return rows as LocalProductImageRecord[];
      } finally {
        db.close();
      }
    },

    async get(id: string) {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_PRODUCT_IMAGES_STORE, 'readonly');
        const store = tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE);
        const row = await requestToPromise(store.get(id));
        await txDone(tx);
        return (row as LocalProductImageRecord | undefined) ?? undefined;
      } finally {
        db.close();
      }
    },

    async put(record: LocalProductImageRecord) {
      await applyImageBatch(factory, { put: [record] });
    },

    async delete(id: string) {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_PRODUCT_IMAGES_STORE, 'readwrite');
        const store = tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE);
        const existing = await requestToPromise(store.get(id));
        if (existing === undefined) {
          await txDone(tx);
          return false;
        }
        store.delete(id);
        await txDone(tx);
        return true;
      } finally {
        db.close();
      }
    },

    async deleteAllForProduct(productId: string) {
      const db = await openLocalProductsDb(factory);
      try {
        const tx = db.transaction(LOCAL_PRODUCT_IMAGES_STORE, 'readwrite');
        const store = tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE);
        const index = store.index(LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX);
        const rows = (await requestToPromise(
          index.getAll(productId),
        )) as LocalProductImageRecord[];
        for (const row of rows) {
          store.delete(row.id);
        }
        await txDone(tx);
        return rows.length;
      } finally {
        db.close();
      }
    },

    async applyBatch(batch: ProductImageBatch) {
      await applyImageBatch(factory, batch);
    },
  };
}

async function applyImageBatch(
  factory: IdbFactory | undefined,
  batch: ProductImageBatch,
): Promise<void> {
  const puts = batch.put ?? [];
  const deleteIds = batch.deleteIds ?? [];
  for (const record of puts) {
    if (!(record.blob instanceof Blob)) {
      throw new Error('Local image put requires a Blob payload.');
    }
  }
  if (puts.length === 0 && deleteIds.length === 0) return;

  const db = await openLocalProductsDb(factory);
  try {
    const tx = db.transaction(LOCAL_PRODUCT_IMAGES_STORE, 'readwrite');
    const store = tx.objectStore(LOCAL_PRODUCT_IMAGES_STORE);
    for (const id of deleteIds) {
      store.delete(id);
    }
    for (const record of puts) {
      store.put(record);
    }
    await txDone(tx);
  } finally {
    db.close();
  }
}
